import React, {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {get} from 'lodash';
import {getSignDocPreviewAPI} from '../../../Services/api';
import {SignDocTypes} from '../SignDocLib';
import {PdfViewer} from '../../../Utils/PdfViewer/PdfViewer';
import {VideoPlayer} from '../../../Utils/VideoPlayer/VideoPlayer';
import CanvaEmbed from '../../../Utils/Canva/CanvaEmbed';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import {PageTitle} from '../../PageTitle/PageTitle';
import {I18nReactContext} from '../../../contexts/I18nContext';
import './SignDocPreviewPage.scss';

/**
 * Admin preview page for SignDoc content.
 * Renders the appropriate viewer (PDF, video, audio, Canva) without tracking progress or recording views.
 *
 * @returns {JSX.Element} The preview page component
 */
export const SignDocPreviewPage = () => {
    const {signDocId} = useParams();
    const {i18n} = useContext(I18nReactContext);
    const [isLoading, setIsLoading] = useState(true);
    const [signDoc, setSignDoc] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPreview = async () => {
            try {
                const result = await getSignDocPreviewAPI({signDocId});
                setSignDoc(result);
            } catch (err) {
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };
        loadPreview();
    }, [signDocId]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error || !signDoc) {
        return (
            <div className="SignDocPreviewPage MenuPage">
                <PageTitle size="minimized" title={i18n.translate('signDocPreviewPage.errorTitle')} noLogo={false} />
                <div className="menuInnerPage">
                    <div className="SignDocPreviewPage__error">
                        {i18n.translate('signDocPreviewPage.errorDescription')}
                    </div>
                </div>
            </div>
        );
    }

    const signDocType = get(signDoc, 'type');
    const signDocName = get(signDoc, 'docName', '');
    const signedUrl = get(signDoc, 'signedUrl');
    const linkUrl = get(signDoc, 'linkUrl');

    const renderViewer = () => {
        switch (signDocType) {
            case SignDocTypes.READ_AND_SIGN_DOC_PDF:
                return <PdfViewer url={signedUrl} onReadingCompleted={() => {}} />;

            case SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE:
            case SignDocTypes.AUDIO_FILE:
                return (
                    <div className="SignDocPreviewPage__video-container">
                        <VideoPlayer
                            videoUrl={signedUrl}
                            startAtDecimalPercent={0}
                            onWatchComplete={() => {}}
                            preventSeeking={false}
                        />
                    </div>
                );

            case SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK:
            case SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK:
                return (
                    <div className="SignDocPreviewPage__video-container">
                        <VideoPlayer
                            videoUrl={linkUrl}
                            startAtDecimalPercent={0}
                            onWatchComplete={() => {}}
                            preventSeeking={false}
                        />
                    </div>
                );

            case SignDocTypes.CANVA_PRESENTATION:
                return (
                    <div className="SignDocPreviewPage__canva-container">
                        <CanvaEmbed canvaLink={linkUrl} onReadingCompleted={() => {}} />
                    </div>
                );

            default:
                return (
                    <div className="SignDocPreviewPage__no-content">
                        {i18n.translate('signDocPreviewPage.noContent')}
                    </div>
                );
        }
    };

    return (
        <div className="SignDocPreviewPage MenuPage">
            <PageTitle
                size="minimized"
                title={`${i18n.translate('signDocPreviewPage.title')} - ${signDocName}`}
                noLogo={false}
            />
            <div className="menuInnerPage">
                <div className="SignDocPreviewPage__content">
                    {renderViewer()}
                </div>
            </div>
        </div>
    );
};
